import { Link } from "react-router-dom";
import { Package, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useApp } from "@/hooks/useApp";
import { pickLang } from "@/lib/i18n";

export interface ProductCardData {
  id: string;
  name: string;
  name_en?: string | null;
  short_description: string;
  short_description_en?: string | null;
  price: number;
  category: string;
  sales_count: number;
  stock: number;
  image_url?: string | null;
  custom_category_id?: string | null;
  custom_category_name_pt?: string | null;
  custom_category_name_en?: string | null;
}

export default function ProductCard({ p }: { p: ProductCardData }) {
  const { t, lang } = useApp();
  const out = p.stock <= 0;
  const name = pickLang(p.name, p.name_en, lang) || p.name;
  const desc = pickLang(p.short_description, p.short_description_en, lang) || p.short_description;
  const catLabel = pickLang(p.custom_category_name_pt ?? "", p.custom_category_name_en ?? "", lang) || p.category;
  return (
    <div className="glow-card rounded-xl overflow-hidden flex flex-col border border-border">
      {p.image_url ? (
        <Link to={`/produto/${p.id}`} aria-label={name} className="block aspect-[16/10] overflow-hidden bg-secondary/40">
          <img src={p.image_url} alt={name} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
        </Link>
      ) : (
        <Link to={`/produto/${p.id}`} aria-label={name} className="block aspect-[16/10] bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/40 flex items-center justify-center">
          <Package className="w-10 h-10 text-muted-foreground/40" aria-hidden="true" />
        </Link>
      )}
      <div className="p-5 flex flex-col gap-4 flex-1">
        <div className="flex items-start justify-between gap-2">
          <Badge variant="secondary" className="capitalize">{catLabel}</Badge>
          {out ? (
            <Badge variant="destructive" className="bg-[hsl(0_75%_38%)] text-white border-transparent">{t("out")}</Badge>
          ) : (
            <Badge className="bg-success/15 text-success border-success/30 hover:bg-success/20"><Package className="w-3 h-3 mr-1" />{p.stock} {t("in_stock")}</Badge>
          )}
        </div>
        <div>
          <h3 className="font-semibold text-lg leading-tight mb-1">{name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{desc}</p>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <TrendingUp className="w-3 h-3" /> {p.sales_count} {t("sales")}
        </div>
        <div className="mt-auto flex items-end justify-between gap-3">
          <div>
            <div className="text-xs text-muted-foreground">{t("from")}</div>
            <div className="text-2xl font-bold gradient-text">R$ {Number(p.price).toFixed(2)}</div>
          </div>
          <Button asChild disabled={out}>
            <Link to={`/produto/${p.id}`}>{out ? t("out") : t("buy")}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
